import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  CompareBars,
  EmptyState,
  Page,
  QueryState,
  RankedBars,
  Stack,
  StatStrip,
  Table,
  ReviewIcon,
  ConnectorIcon,
  GlobeIcon,
  TrendIcon,
  CheckCircleIcon,
} from '@integra/ui';
import { useDestinations, useWeeklyReview } from '../api/hooks.js';
import type {
  Destination,
  SourceMetricRollup,
  WeeklyReviewChannel,
  WeeklyReviewRanking,
  WeeklyReviewSignalFigure,
} from '../api/types.js';

/**
 * Weekly review (§20.5). A content calendar runs a weekly review — read the
 * per-channel leading signals, then cut the weakest slot types and double the
 * strongest. This screen is the MEASUREMENT that makes that call evidence-based
 * (§2.4): it RANKS measured performance so a human can see what to cut or
 * double. It does not recommend — that intelligence is HIVE's, not INTEGRA's.
 *
 * §2.7 binds hardest here: a measured figure and an estimated figure are never
 * blended. Ranking is on measured values alone; estimates ride alongside,
 * clearly labelled, and never move a row's position.
 */

const numberFormat = new Intl.NumberFormat();

function formatNumber(value: number): string {
  return numberFormat.format(value);
}

function humanMetric(metric: string): string {
  return metric.replace(/_/g, ' ');
}

const DIMENSION_LABEL: Record<string, string> = {
  campaign: 'Campaigns',
  asset: 'Assets',
  assetType: 'Asset types',
};

/** Human label for a source connector id in the site-analytics section. */
const SOURCE_LABELS: Record<string, string> = {
  'google-analytics': 'Google Analytics 4',
};
function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

/**
 * Site & audience review: a source's OWN measured figures over the week — a GA4
 * property's sessions, users, page views. Grouped per property and shown as
 * plain measured/estimated Stats. It sits ALONGSIDE the channel reviews and is
 * never ranked among them: a website session is not a channel post's
 * measurement, so it never competes for a "double / cut" position (§2.7).
 */
function SiteReview({ sources }: { sources: SourceMetricRollup[] }) {
  const groups = new Map<
    string,
    { source: string; externalId: string | null; rows: SourceMetricRollup[] }
  >();
  for (const row of sources) {
    const key = `${row.source}:${row.externalId ?? ''}`;
    const group = groups.get(key) ?? { source: row.source, externalId: row.externalId, rows: [] };
    group.rows.push(row);
    groups.set(key, group);
  }

  return (
    <Stack gap="md">
      {[...groups.values()].map((group) => (
        <Card
          key={`${group.source}:${group.externalId ?? ''}`}
          title={sourceLabel(group.source)}
          subtitle={
            group.externalId !== null
              ? `Property ${group.externalId} — site-wide, not attributed to any channel.`
              : 'Site-wide, not attributed to any channel.'
          }
        >
          <CompareBars
            groups={group.rows.map((row) => ({
              label: humanMetric(row.metric),
              measured: row.measured,
              estimated: row.estimated,
            }))}
            format={formatNumber}
          />
        </Card>
      ))}
    </Stack>
  );
}

function channelLabel(channel: WeeklyReviewChannel, destination: Destination | undefined): string {
  const name = channel.connectorConfigId ?? channel.destinationId;
  return destination !== undefined ? `${name} (${destination.status})` : name;
}

/** The leading signal and every other measured/estimated figure, kept apart. */
function ChannelSignals({ signals }: { signals: WeeklyReviewSignalFigure[] }) {
  if (signals.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-edge-strong bg-surface px-4 py-3">
        <p className="text-sm font-semibold text-navy-700">No aggregable signal for this window</p>
        <p className="mt-1 text-xs text-ink-muted">
          This channel reported no additive metric in the period. That is an absence of data, not a
          result of zero.
        </p>
      </div>
    );
  }

  return (
    <CompareBars
      groups={signals.map((signal) => ({
        label: humanMetric(signal.metric),
        measured: signal.measured,
        estimated: signal.estimated,
      }))}
      format={formatNumber}
    />
  );
}

/**
 * A measured ranking for one dimension. Strongest measured performer first,
 * weakest last — the two ends are what a reviewer weighs when deciding what to
 * double and what to cut. The badges describe the MEASUREMENT ("Highest
 * measured" / "Lowest measured"); they are not advice.
 */
function RankingTable({ ranking }: { ranking: WeeklyReviewRanking }) {
  const hasEstimate = ranking.entries.some((e) => e.estimated !== null);
  return (
    <Card
      title={DIMENSION_LABEL[ranking.dimension] ?? ranking.dimension}
      subtitle={`Ranked by measured ${humanMetric(ranking.rankedBy)} — longest bar is the strongest slot, shortest the weakest.`}
    >
      <Stack gap="sm">
        <RankedBars
          items={ranking.entries.map((entry) => ({
            label: entry.label,
            value: entry.measured,
            valueLabel: formatNumber(entry.measured),
          }))}
        />
        {hasEstimate && (
          <p className="text-xs text-ink-muted">
            Ranked on measured values only — estimates are shown on Performance and are never summed
            into the rank (§2.7).
          </p>
        )}
      </Stack>
    </Card>
  );
}

function ChannelReview({
  channel,
  destination,
}: {
  channel: WeeklyReviewChannel;
  destination: Destination | undefined;
}) {
  return (
    <Card
      title={channelLabel(channel, destination)}
      subtitle={
        channel.leadingSignal !== null
          ? `Leading measured signal: ${humanMetric(channel.leadingSignal)}.`
          : 'No measured signal reported for this window.'
      }
    >
      <Stack gap="lg">
        {!channel.hasMeasuredData && (
          <Alert tone="warning" title="No measurement for this channel">
            Nothing was measured on this channel in the window. Ranking is on measured performance
            only, so there is nothing to rank. This is missing data, not zero performance.
          </Alert>
        )}

        <ChannelSignals signals={channel.signals} />

        {channel.nonAdditiveSignalsPresent.length > 0 && (
          <p className="text-xs text-ink-muted">
            Rate signals present but not aggregated:{' '}
            {channel.nonAdditiveSignalsPresent.map(humanMetric).join(', ')}. Rates are not additive,
            so INTEGRA does not sum them across the window.
          </p>
        )}

        {channel.rankings.length > 0 ? (
          <Stack gap="md">
            {channel.rankings.map((ranking) => (
              <RankingTable key={ranking.dimension} ranking={ranking} />
            ))}
          </Stack>
        ) : (
          channel.hasMeasuredData && (
            <p className="text-xs text-ink-muted">
              No campaign or asset carried a measured value for the leading signal, so there is
              nothing to rank yet.
            </p>
          )
        )}
      </Stack>
    </Card>
  );
}

export function WeeklyReviewPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();

  const review = useWeeklyReview(workspaceId);
  const destinations = useDestinations(workspaceId);

  const destinationById = new Map(
    (destinations.data?.data ?? []).map((destination) => [destination.id, destination]),
  );

  const channels = review.data?.channels ?? [];
  const dimensions = review.data?.dimensions ?? [];
  const sources = review.data?.sources ?? [];

  return (
    <Page
      title="Weekly review"
      icon={ReviewIcon}
      description="Rank this week's measured performance per channel so you cut the weakest content slots and double the strongest."
      stats={
        channels.length > 0 ? (
          <StatStrip
            stats={[
              { icon: ConnectorIcon, label: 'Channels reviewed', value: channels.length },
              { icon: GlobeIcon, label: 'Sources', value: sources.length, tone: 'accent' },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <QueryState loading={review.isLoading} error={review.error} what="the weekly review">
          {channels.length === 0 ? (
            <EmptyState
              icon={ReviewIcon}
              title="Nothing measured this week"
              description="No channel reported performance data in the trailing 7 days. Run a collection from the Dashboard, or check that a destination exposes a metrics API."
              features={[
                { icon: TrendIcon, label: 'Ranks strongest and weakest slots' },
                { icon: ConnectorIcon, label: 'Reads every connected channel' },
                { icon: CheckCircleIcon, label: 'Ranked on measured data only' },
              ]}
            />
          ) : (
            <Stack gap="lg">
              {review.data !== undefined && (
                <p className="text-xs text-ink-muted">
                  Window {new Date(review.data.window.from).toLocaleDateString()} –{' '}
                  {new Date(review.data.window.to).toLocaleDateString()}
                </p>
              )}
              {channels.map((channel) => (
                <ChannelReview
                  key={channel.destinationId}
                  channel={channel}
                  destination={destinationById.get(channel.destinationId)}
                />
              ))}
            </Stack>
          )}
        </QueryState>

        {/* Website & audience: source-level (GA4) figures over the same week,
            shown alongside the channels but never ranked among them (§2.7). */}
        {sources.length > 0 && (
          <Stack gap="md">
            <div className="border-t border-edge pt-6">
              <h2 className="font-heading text-lg text-navy-700">Website &amp; audience</h2>
              <p className="text-xs text-ink-muted">
                What a connected source (e.g. Google Analytics 4) measured site-wide this week.
                Shown beside the channels above, never ranked among them — a website session is not
                a channel post&rsquo;s performance.
              </p>
            </div>
            <SiteReview sources={sources} />
          </Stack>
        )}

        {dimensions.length > 0 && (
          <Card
            title="What can be ranked"
            subtitle="The dimensions this review can and cannot rank by, and why."
          >
            <Table
              columns={[
                {
                  key: 'dimension',
                  header: 'Dimension',
                  sortValue: (row: { dimension: string }) => row.dimension,
                  render: (row: { dimension: string }) => humanMetric(row.dimension),
                },
                {
                  key: 'available',
                  header: 'Rankable',
                  sortValue: (row: { available: boolean }) => (row.available ? 1 : 0),
                  render: (row: { available: boolean }) => (
                    <Badge tone={row.available ? 'success' : 'neutral'} dot>
                      {row.available ? 'Yes' : 'No'}
                    </Badge>
                  ),
                },
                {
                  key: 'reason',
                  header: 'Why',
                  render: (row: { reason: string }) => (
                    <span className="text-xs text-ink-muted">{row.reason}</span>
                  ),
                },
              ]}
              rows={dimensions}
              rowKey={(row) => row.dimension}
            />
          </Card>
        )}
      </Stack>
    </Page>
  );
}
